import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faStore,
  faRotateRight,
  faCircleExclamation,
  faTriangleExclamation,
  faCircleInfo,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import desktopApi, { describeError } from './desktopApi';
import { formatDate, timeAgo, MODE_LABELS, APPROVAL_LABELS } from './desktopFormat';
import './Desktop.css';

const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'attention', label: 'À traiter' },
  { id: 'online', label: 'En ligne' },
  { id: 'local', label: 'Locales' },
  { id: 'pending', label: 'En attente' },
  { id: 'blocked', label: 'Bloquées' },
];

const ATTENTION_ICON = { 3: faCircleExclamation, 2: faTriangleExclamation, 1: faCircleInfo };
const ATTENTION_CLASS = { 3: 'sev-critical', 2: 'sev-warning', 1: 'sev-info' };

export const DeviceGauge = ({ used, max }) => {
  const ratio = max > 0 ? Math.min(used / max, 1) : 0;
  const level = used >= max ? 'full' : ratio >= 0.75 ? 'high' : 'ok';
  return (
    <span className="dk-gauge" title={`${used} appareil(s) sur ${max} autorisé(s)`}>
      <span className="dk-gauge-text">{used}/{max}</span>
      <span className="dk-gauge-track"><span className={`dk-gauge-fill ${level}`} style={{ width: `${ratio * 100}%` }} /></span>
    </span>
  );
};

export const ShopBadges = ({ shop }) => (
  <>
    <span className={`dk-badge mode-${shop.mode}`}>{MODE_LABELS[shop.mode] || shop.mode}</span>
    {shop.approvalStatus !== 'approved' && (
      <span className={`dk-badge approval-${shop.approvalStatus}`}>{APPROVAL_LABELS[shop.approvalStatus] || shop.approvalStatus}</span>
    )}
    {shop.isBlocked && <span className="dk-badge blocked">Bloquée</span>}
  </>
);

// Every Lonnii Desktop shop, problems first. A row opens the shop and its devices.
const DesktopShops = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') || 'all';
  const [search, setSearch] = useState(params.get('search') || '');
  const [shops, setShops] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setShops(await desktopApi.shops({ search, filter }));
      setError(null);
    } catch (e) {
      setError(describeError(e));
      setShops([]);
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => {
    // Typing waits a moment so every keystroke is not a request.
    const wait = setTimeout(load, 300);
    return () => clearTimeout(wait);
  }, [load]);

  const setFilter = (id) => {
    const next = new URLSearchParams(params);
    if (id === 'all') next.delete('filter'); else next.set('filter', id);
    setParams(next, { replace: true });
  };

  const open = (shop) => navigate(`/desktop/shops/${shop.id}`);

  return (
    <div className="dk-page">
      <div className="dk-header">
        <div>
          <h1>Boutiques</h1>
          <p>Les boutiques qui utilisent Lonnii Desktop, les plus urgentes en premier.</p>
        </div>
        <div className="dk-header-actions">
          <button className="dk-btn" onClick={load} disabled={loading}>
            <FontAwesomeIcon icon={faRotateRight} spin={loading} /> Actualiser
          </button>
        </div>
      </div>

      <div className="dk-toolbar">
        <div className="dk-search">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="search"
            placeholder="Rechercher par nom ou identifiant…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="dk-chips" role="group" aria-label="Filtrer">
          {FILTERS.map((f) => (
            <button key={f.id} className={filter === f.id ? 'on' : ''} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="dk-error">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{error}</span>
          <button className="dk-btn" onClick={load}>Réessayer</button>
        </div>
      )}

      {loading && shops.length === 0 ? (
        <div className="dk-loading"><div className="spinner"></div><p>Chargement...</p></div>
      ) : !error && shops.length === 0 ? (
        <div className="dk-card dk-empty">
          <FontAwesomeIcon icon={faStore} />
          <p>{search || filter !== 'all' ? 'Aucune boutique ne correspond.' : 'Aucune boutique Lonnii Desktop pour le moment.'}</p>
        </div>
      ) : shops.length > 0 && (
        <div className="dk-card dk-table-wrap">
          <table className="dk-table">
            <thead>
              <tr>
                <th aria-label="Priorité"></th>
                <th>Boutique</th>
                <th>Appareils</th>
                <th>Dernière activité</th>
                <th>Version</th>
                <th>Abonnement</th>
                <th>Sauvegarde</th>
                <th aria-label="Ouvrir"></th>
              </tr>
            </thead>
            <tbody>
              {shops.map((shop) => (
                <tr key={shop.id} onClick={() => open(shop)} tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && open(shop)}>
                  <td className="dk-col-flag" data-label="">
                    {shop.attentionLevel > 0 && (
                      <FontAwesomeIcon icon={ATTENTION_ICON[shop.attentionLevel]} className={ATTENTION_CLASS[shop.attentionLevel]} />
                    )}
                  </td>
                  <td className="dk-col-name" data-label="Boutique">
                    <strong>{shop.name}</strong>
                    <span className="dk-badges"><ShopBadges shop={shop} /></span>
                  </td>
                  <td data-label="Appareils"><DeviceGauge used={shop.activeDevices} max={shop.maxDevices} /></td>
                  <td data-label="Dernière activité">{timeAgo(shop.lastActivityAt)}</td>
                  <td data-label="Version">{shop.appVersion || '—'}</td>
                  <td data-label="Abonnement">{shop.mode === 'online' ? (shop.subscriptionEndsAt ? `jusqu'au ${formatDate(shop.subscriptionEndsAt)}` : 'Aucun') : '—'}</td>
                  <td data-label="Sauvegarde">{shop.mode === 'online' ? timeAgo(shop.lastBackupAt) : '—'}</td>
                  <td className="dk-col-go"><FontAwesomeIcon icon={faChevronRight} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DesktopShops;
